// src/services/axiosSetup.js
import axios, { AxiosError } from 'axios';
import useAuthStore, { refreshToken } from "@/stores/auth.store.js";
import router from '@/router';
import { errorToast } from './toast.service';
import { locked } from '@/utils';

// To avoid multiple "token expired" messages and redirects to the login page
let tokenExpiredRedirectActive = false;

// To avoid multiple requests to the refresh-token api at the same time
const RESET_REFRESH_API_DELAY = 10000;
const refreshTokenLocked = locked(refreshToken, RESET_REFRESH_API_DELAY);

axios.defaults.baseURL = import.meta.env.VITE_API_URL;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

axios.interceptors.request.use((config) => {
  const authStore = useAuthStore(); // Get the auth store
  const token = authStore.user?.access_token;
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

axios.interceptors.response.use(
  response => response,
  handleTokenAuth // function that gets passed an AxiosError
);

/**
 * Handles errors related to the token expiration
 * @param {AxiosError} error
 */
async function handleTokenAuth(error) {
  if (
    error.response?.status != 401
    || error.config._retry
    || error.config.url?.includes('/refresh-token')
  ) {
    return Promise.reject(error);
  }

  // Executes only one request to the refresh-token api, otherwise
  // it just waits for it to conclude
  let hasUpdatedToken = await refreshTokenLocked();

  // If the refresh token has issues (malformed or expired)
  if (!hasUpdatedToken) {
    redirectWithNotice();
    return Promise.reject(error);
  }

  // Retry request
  error.config._retry = true;
  return await axios.request(error.config);
}

function redirectWithNotice() {
  // If the function was already executed, has logged you out and
  // is only waiting to redirect you, don't do anything.
  if (tokenExpiredRedirectActive) return;
  
  const authStore = useAuthStore();
  authStore.logout();

  errorToast("Il token è scaduto. A breve verrete reindirizzati alla pagina di login.");
  tokenExpiredRedirectActive = true;

  setTimeout(() => {
    tokenExpiredRedirectActive = false;
    router.replace({ name: "login" });
  }, 4000);
}

export default axios;
